/**
 * spec グラフのエッジ語彙 26 型（allowlist）と、型別の必須エッジ。
 *
 * 合法なエッジは NE §2 の 26 型のみ。エッジ名は 1 意味 1 名前で一意（決定 l）、命名は
 * `<関係動詞句>_<TARGET 型名>` に統一する（決定 o）。語彙の追加は cortex への PR
 * （契約変更）でのみ行う（制約 1）。
 *
 * エッジは独立した書込対象ではない — 26 型のうち 20 型は source 側ノードの宣言の欄の射影、
 * 1 型は集約内の定義位置、5 型は複数の欄やグラフ全体の突き合わせから変換層が導出するもので、
 * 公開 API はエッジを受け取らない（API §1）。変換層が集約由来のものを全て再導出して置き換える
 * （derive & replace）。
 *
 * ハブが業務に戻り、業務ロジックが処理（`Rule`）への参照の並びになったので、実装・DB への
 * 接地は処理が持ち、部品は業務を指す（NE §0）。どの型が何本以上のエッジを持つべきかは
 * `SPEC_REQUIRED_EDGES` が NE §4 の行ごとに宣言する。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business spec グラフのエッジ語彙 26 型 allowlist と、各型の方向・多重度・出自・型別の必須エッジの宣言的メタデータ
 * @graph-connects none
 */
import { SPEC_NODE_TYPES } from './node-types.js';
/**
 * エッジ語彙 26 型の閉集合（NE §2。並びは同節の表の順）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business エッジ 26 型の allowlist。BQ の edgeType 列に入る値の閉集合
 * @graph-connects none
 */
export const SPEC_EDGE_TYPES = [
    'STARTS_AT',
    'FOLLOWS',
    'EXITS_AT',
    'BELONGS_TO_STAGE',
    'INCLUDES_BUSINESS',
    'ENTERS_AT',
    'HANDS_OFF_TO_BUSINESS',
    'RETURNS_TO_STAGE',
    'FEEDS_BUSINESS',
    'OBSERVES_BUSINESS',
    'PERFORMED_BY_ACTOR',
    'BELONGS_TO_DOMAIN',
    'USES_RULE',
    'READS_RULE',
    'IMPLEMENTED_BY_API',
    'IMPLEMENTED_BY_TASK',
    'DEFINED_IN_SCREEN',
    'OPERATES_BUSINESS',
    'SHOWN_WHEN_RULE',
    'TRANSITIONS_TO_SCREEN',
    'HAS_VALUE_TERM',
    'PROPERTY_OF_TERM',
    'MEASURED_IN_UNIT',
    'USES_TERM',
    'USES_TABLE',
    'USES_COLUMN',
];
/**
 * エッジ 26 型の宣言的定義表。方向・多重度・出自・宣言元の欄の単一ソース。
 *
 * 配列順は `SPEC_EDGE_TYPES` と同じ（NE §2 の表の順）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business エッジ 26 型の方向・多重度・出自・宣言元の欄を 1 箇所に集約した定義表（derive & replace の写像元）
 * @graph-connects none
 */
export const SPEC_EDGE_DEFINITIONS = [
    {
        // ステージの入口。前のステージの出口と同じ結末の行を指す（分岐のステージは同じ業務の別の
        // 結末の行）。どの結末の行から入るか（`entry.ending`）はエッジの属性に載せる。最初のステージと
        // 横断ステージは `entry` を持たないので張らない。
        edgeType: 'STARTS_AT',
        sourceTypes: ['Stage'],
        targetType: 'Business',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'entry',
    },
    {
        // 前のステージ。STARTS_AT の業務を EXITS_AT に持つステージを指す。ステージ全体を突き合わせないと
        // 決まらないので、書き手は宣言しない（`deriveStageChains`）。
        edgeType: 'FOLLOWS',
        sourceTypes: ['Stage'],
        targetType: 'Stage',
        multiplicity: 'N:1',
        origin: 'derivation',
        declaredBy: null,
    },
    {
        // エッジの同一性は（型, 起点, 終点）なので、どの結末の行で区切るか（`exit.ending`）は
        // エッジの属性に載せないと失われる（NE §2）。横断ステージは `exit` を持たないので張らない。
        edgeType: 'EXITS_AT',
        sourceTypes: ['Stage'],
        targetType: 'Business',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'exit',
    },
    {
        edgeType: 'BELONGS_TO_STAGE',
        sourceTypes: ['Flow'],
        targetType: 'Stage',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'stage',
    },
    {
        // 同じ業務が複数のフローに出て、1 本のフローが複数の業務を並べる。
        edgeType: 'INCLUDES_BUSINESS',
        sourceTypes: ['Flow'],
        targetType: 'Business',
        multiplicity: 'N:M',
        origin: 'declaration',
        declaredBy: 'nodes[].business',
    },
    {
        // 主フローの入口（前のステージの出口の結末）と、引き金が「主フローの結末」の支援フローだけが
        // 張る。時間・操作・外部の引き金は業務を指さないので張らない。どの結末の行から入るか
        // （`entry.ending`）はエッジの属性に載せる。
        edgeType: 'ENTERS_AT',
        sourceTypes: ['Flow'],
        targetType: 'Business',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'entry',
    },
    {
        // 入れる型の出口。渡す先は値を読む主フローの業務。
        edgeType: 'HANDS_OFF_TO_BUSINESS',
        sourceTypes: ['Flow'],
        targetType: 'Business',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'exit',
    },
    {
        // 割り込み型の出口のうち、前のステージの入口へ戻すものだけが張る。中止は張らない。
        edgeType: 'RETURNS_TO_STAGE',
        sourceTypes: ['Flow'],
        targetType: 'Stage',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'exit',
    },
    {
        // 入れる型の支援フロー。フローの業務が変える先を、主フローの業務の判定表が読むときに立つ。
        // 両側の集約を突き合わせないと決まらないので、書き手は宣言しない。
        edgeType: 'FEEDS_BUSINESS',
        sourceTypes: ['Flow'],
        targetType: 'Business',
        multiplicity: 'N:M',
        origin: 'derivation',
        declaredBy: null,
    },
    {
        // 出す型の支援フロー。FEEDS_BUSINESS の読み書きの向きを逆にした突き合わせで立つ。
        edgeType: 'OBSERVES_BUSINESS',
        sourceTypes: ['Flow'],
        targetType: 'Business',
        multiplicity: 'N:M',
        origin: 'derivation',
        declaredBy: null,
    },
    {
        edgeType: 'PERFORMED_BY_ACTOR',
        sourceTypes: ['Business'],
        targetType: 'Actor',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'actor',
    },
    {
        edgeType: 'BELONGS_TO_DOMAIN',
        sourceTypes: ['Business'],
        targetType: 'Domain',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'domain',
    },
    {
        // 同じ業務ルールが複数の業務から使われ、1 業務が集合を持つので両側が「多」になる。
        edgeType: 'USES_RULE',
        sourceTypes: ['Business'],
        targetType: 'Rule',
        multiplicity: 'N:M',
        origin: 'declaration',
        declaredBy: 'rules',
    },
    {
        // 業務ルールの依存。入力が別の業務ルールの output と一致するときに立つ。書き手は宣言しない。
        edgeType: 'READS_RULE',
        sourceTypes: ['Rule'],
        targetType: 'Rule',
        multiplicity: 'N:M',
        origin: 'derivation',
        declaredBy: null,
    },
    {
        // 処理をどこまで割っても実装は 1 つに定まらない（1 本の API が複数の処理から指される）ので N:M。
        edgeType: 'IMPLEMENTED_BY_API',
        sourceTypes: ['Rule'],
        targetType: 'Api',
        multiplicity: 'N:M',
        origin: 'declaration',
        declaredBy: 'implementedBy',
    },
    {
        edgeType: 'IMPLEMENTED_BY_TASK',
        sourceTypes: ['Rule'],
        targetType: 'Task',
        multiplicity: 'N:M',
        origin: 'declaration',
        declaredBy: 'implementedBy',
    },
    {
        edgeType: 'DEFINED_IN_SCREEN',
        sourceTypes: ['Component'],
        targetType: 'Screen',
        multiplicity: 'N:1',
        origin: 'path-or-definition',
        declaredBy: null,
    },
    {
        // 部品 → 業務。宣言は部品側の単数の欄が持つ。target 側の重複は許す（1 業務を複数の画面の
        // 部品が操作しうる）。
        edgeType: 'OPERATES_BUSINESS',
        sourceTypes: ['Component'],
        targetType: 'Business',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'operates',
    },
    {
        // 部品の出し分けは判定表（処理）への参照で表す。自由文の表示条件は持たない。
        edgeType: 'SHOWN_WHEN_RULE',
        sourceTypes: ['Component'],
        targetType: 'Rule',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'visibleWhen',
    },
    {
        edgeType: 'TRANSITIONS_TO_SCREEN',
        sourceTypes: ['Component'],
        targetType: 'Screen',
        multiplicity: 'N:M',
        origin: 'declaration',
        declaredBy: 'transitionTo / actions[].transitionTo / actions[].onErrorTransitionTo',
    },
    {
        // プロパティの用語 → その取りうる値の用語。1 つの値の用語を複数のプロパティが指してよい
        // （「該当する / 該当しない」は多くの真偽が共有する）ので N:M。
        edgeType: 'HAS_VALUE_TERM',
        sourceTypes: ['Term'],
        targetType: 'Term',
        multiplicity: 'N:M',
        origin: 'declaration',
        declaredBy: 'values',
    },
    {
        // プロパティの用語 → 属するクラスの用語。プロパティは 1 つのクラスに属する。
        edgeType: 'PROPERTY_OF_TERM',
        sourceTypes: ['Term'],
        targetType: 'Term',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'ownerClass',
    },
    {
        // 数を表すプロパティの用語 → 読み方を決める単位。単位は 1 つに定まる。
        edgeType: 'MEASURED_IN_UNIT',
        sourceTypes: ['Term'],
        targetType: 'Unit',
        multiplicity: 'N:1',
        origin: 'declaration',
        declaredBy: 'unit',
    },
    {
        // 用語はどの仕様からも引かれ、1 つのノードの中でも複数の欄（名前・中身・入力・出力・結末・
        // 説明文）から引かれる。欄 1 つには決まらないので導出に置く。PJ は仕様の中身ではないので
        // 起点にならない。
        edgeType: 'USES_TERM',
        sourceTypes: SPEC_NODE_TYPES,
        targetType: 'Term',
        multiplicity: 'N:M',
        origin: 'derivation',
        declaredBy: null,
    },
    {
        // DB 参照は処理が持つ（決定。2026-09-21）。
        edgeType: 'USES_TABLE',
        sourceTypes: ['Rule'],
        targetType: 'Table',
        multiplicity: 'N:M',
        origin: 'declaration',
        declaredBy: 'usesTable',
    },
    {
        edgeType: 'USES_COLUMN',
        sourceTypes: ['Rule'],
        targetType: 'Column',
        multiplicity: 'N:M',
        origin: 'declaration',
        declaredBy: 'usesColumn',
    },
];
/**
 * 型ごとの必須エッジ（NE §4。行の並びは同節の表の順で 9 行）。全型がエッジを 1 本以上持つ。
 *
 * 主フローの出口の線（出口の結末 ＝ ステージの `EXITS_AT` と同じ業務）と、動詞の「名前の作り
 * からの参照」は、それを表すエッジ型が 26 型に無いので `when` の条件の文で持つ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 型ごとに 1 本以上立つべきエッジ（out / in）と、0 本を許すエッジの宣言。監査が孤立したノードを数える基準
 * @graph-connects none
 */
export const SPEC_REQUIRED_EDGES = [
    {
        nodeTypes: ['Stage'],
        requirements: [
            { direction: 'out', anyOf: ['STARTS_AT'], when: '最初のステージと横断ステージ以外' },
            { direction: 'out', anyOf: ['EXITS_AT'], when: '横断ステージ以外' },
            { direction: 'in', anyOf: ['BELONGS_TO_STAGE'], when: null },
        ],
        zeroAllowed: ['RETURNS_TO_STAGE', 'FOLLOWS'],
    },
    {
        nodeTypes: ['Flow'],
        requirements: [
            { direction: 'out', anyOf: ['BELONGS_TO_STAGE'], when: null },
            { direction: 'out', anyOf: ['INCLUDES_BUSINESS'], when: null },
            { direction: 'out', anyOf: ['HANDS_OFF_TO_BUSINESS'], when: '入れる型' },
            { direction: 'out', anyOf: ['RETURNS_TO_STAGE'], when: '割り込み型で前のステージへ戻す' },
            {
                direction: 'out',
                anyOf: [],
                when: '主フローは出口の結末がステージの EXITS_AT と同じ業務（表すエッジ型は無い）',
            },
        ],
        zeroAllowed: ['ENTERS_AT', 'FEEDS_BUSINESS', 'OBSERVES_BUSINESS'],
    },
    {
        nodeTypes: ['Business'],
        requirements: [
            { direction: 'out', anyOf: ['PERFORMED_BY_ACTOR'], when: null },
            { direction: 'out', anyOf: ['BELONGS_TO_DOMAIN'], when: null },
            { direction: 'out', anyOf: ['USES_RULE'], when: null },
            { direction: 'in', anyOf: ['INCLUDES_BUSINESS'], when: null },
        ],
        zeroAllowed: ['OPERATES_BUSINESS', 'EXITS_AT'],
    },
    {
        nodeTypes: ['Rule'],
        requirements: [
            { direction: 'out', anyOf: ['IMPLEMENTED_BY_API', 'IMPLEMENTED_BY_TASK'], when: null },
            { direction: 'in', anyOf: ['USES_RULE', 'SHOWN_WHEN_RULE'], when: null },
        ],
        zeroAllowed: [],
    },
    {
        nodeTypes: ['Component'],
        requirements: [{ direction: 'out', anyOf: ['DEFINED_IN_SCREEN'], when: null }],
        zeroAllowed: ['OPERATES_BUSINESS', 'TRANSITIONS_TO_SCREEN'],
    },
    {
        nodeTypes: ['Screen'],
        requirements: [{ direction: 'in', anyOf: ['DEFINED_IN_SCREEN'], when: null }],
        zeroAllowed: [],
    },
    {
        nodeTypes: ['Term', 'Verb', 'Unit'],
        requirements: [
            {
                direction: 'in',
                anyOf: ['USES_TERM', 'MEASURED_IN_UNIT'],
                when: '名前の作りからの参照も数える。定義直後の 0 本は書込を止めず、監査の削除候補に出す',
            },
        ],
        zeroAllowed: [],
    },
    {
        nodeTypes: ['Actor', 'Domain'],
        requirements: [
            { direction: 'in', anyOf: ['PERFORMED_BY_ACTOR', 'BELONGS_TO_DOMAIN'], when: null },
        ],
        zeroAllowed: [],
    },
];
//# sourceMappingURL=edge-types.js.map